import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { ChevronUp, X } from "lucide-react";
import { cn } from "@slumbr/ui";
import type { DockWidget } from "@slumbr/app-sdk";

import { useDockStore } from "./dock-store";
import { useVisibleWidgets } from "./use-dock";
import { DockErrorBoundary } from "./dock-error-boundary";

/**
 * Mobile quick-actions dock.
 *
 * A horizontally snapping strip of widget cards above the bottom nav. Each
 * card names its widget and carries one line of live detail, and the next
 * card peeks in from the edge so the strip reads as scrollable at a glance —
 * the previous single-line version looked like a stray chat message and gave
 * no hint that other widgets existed.
 */
export function DockMobile() {
  const widgets = useVisibleWidgets();
  const mobileIndex = useDockStore((s) => s.mobileIndex);
  const setMobileIndex = useDockStore((s) => s.setMobileIndex);
  const [sheetWidget, setSheetWidget] = useState<DockWidget | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const index = Math.min(Math.max(mobileIndex, 0), Math.max(0, widgets.length - 1));

  // Keep the store's page in step with wherever the user scrolled to.
  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const centre = scroller.scrollLeft + scroller.clientWidth / 2;
    let nearest = 0;
    let best = Infinity;
    cardRefs.current.forEach((card, i) => {
      if (!card) return;
      const cardCentre = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCentre - centre);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    setMobileIndex(nearest);
  }, [setMobileIndex]);

  // Restore the remembered page when the widget set changes.
  useEffect(() => {
    cardRefs.current[index]?.scrollIntoView({ block: "nearest", inline: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets.length]);

  if (widgets.length === 0) return null;

  return (
    <>
      <div className="shrink-0 border-t border-white/[0.06] bg-[#0d0e14]/95 pb-1 pt-2 backdrop-blur-xl">
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {widgets.map((widget, i) => (
            <WidgetCard
              key={widget.id}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              widget={widget}
              onOpen={() => setSheetWidget(widget)}
              only={widgets.length === 1}
            />
          ))}
        </div>

        {widgets.length > 1 && (
          <div className="mt-1.5 flex items-center justify-center gap-1.5">
            {widgets.map((w, i) => (
              <button
                key={w.id}
                type="button"
                aria-label={`Show ${w.title}`}
                onClick={() =>
                  cardRefs.current[i]?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "center",
                  })
                }
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === index ? "w-4 bg-[#ff8fac]" : "w-1.5 bg-white/25"
                )}
              />
            ))}
          </div>
        )}
      </div>

      {sheetWidget && (
        <div
          className="fixed inset-0 z-[140] flex flex-col justify-end"
          onClick={() => setSheetWidget(null)}
        >
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
          <div
            className="animate-slide-up relative max-h-[88dvh] overflow-hidden rounded-t-3xl border-t border-white/10 bg-[#101118] pb-[env(safe-area-inset-bottom)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pb-1 pt-2.5">
              <span className="h-1 w-10 rounded-full bg-white/25" aria-hidden />
            </div>
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3">
              <span className="flex items-center gap-2 text-sm font-black text-white">
                <span className="text-base">{sheetWidget.icon}</span>
                {sheetWidget.title}
              </span>
              <button
                type="button"
                onClick={() => setSheetWidget(null)}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#8b93c7] transition hover:bg-white/[0.06] hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[calc(88dvh-4.5rem-env(safe-area-inset-bottom))] overflow-auto">
              <DockErrorBoundary>
                <sheetWidget.PanelUI />
              </DockErrorBoundary>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const WidgetCard = forwardRef<
  HTMLButtonElement,
  { widget: DockWidget; onOpen: () => void; only: boolean }
>(function WidgetCard({ widget, onOpen, only }, ref) {
  const Compact = widget.CompactUI;
  const count = widget.badge?.();

  return (
    <button
      ref={ref}
      type="button"
      onClick={onOpen}
      className={cn(
        "flex shrink-0 snap-center items-center gap-2.5 rounded-2xl bg-white/[0.05] px-3 py-2 text-left ring-1 ring-inset ring-white/[0.07] transition active:scale-[0.99]",
        // Leave a sliver of the next card showing — that peek is the clearest
        // signal that the strip scrolls.
        only ? "w-full" : "w-[88%]"
      )}
    >
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-base">
        {widget.icon}
        {count != null && count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-[#0d0e14] bg-[#ff477e] px-1 text-[9px] font-black text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-bold leading-tight text-[#e7e9ea]">
          {widget.title}
        </span>
        <span className="mt-0.5 block truncate text-[11.5px] leading-tight text-[#8b93c7]">
          <DockErrorBoundary fallback={<span>Tap to open</span>}>
            {Compact ? <Compact /> : <span>Tap to open</span>}
          </DockErrorBoundary>
        </span>
      </span>

      <ChevronUp className="h-4 w-4 shrink-0 text-[#5f6b78]" />
    </button>
  );
});
